import {
  LitElement,
  html,
  css,
  repeat,
  ref,
  createRef,
  unsafeHTML,
} from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

/**
 * Sets the value of an input field with the specified name.
 *
 * @param {string} name - The name of the input field.
 * @param {string} value - The value to set for the input field.
 * @param {HTMLElement} [context=document] - The context element to search for the input field. Defaults to the document.
 */
function setInput(name, value, context) {
  if (!context) {
    context = document;
  }
  const input = context.querySelector(`input[name="${name}"]`);
  if (input) {
    input.value = value;
  }
}

/**
 * When you're reaching for event.target.closest(selector) but the the search comes up empty
 * because event target is a shadow dom element.
 */
function eventClosest(e, selector) {
  return e
    .composedPath()
    .find((el) => el instanceof HTMLElement && el.matches(selector));
}

/* Given a shopify gid like "gid://shopify/Product/7999627395288" returns the id */
function gidToExternalId(gid) {
  return gid.split("/").pop();
}

/**
 * Web components are rendered in a shadow dom which isolates them from global styles. This class
 * extends LitElement to automatically inject global stylesheets into the shadow dom.
 * Disadvantage: Unaffected by global styles.
 * Advantage: Slots work within these elements.
 */
class LitElementStyled extends LitElement {
  firstUpdated() {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = document.getElementById("stylesheet-link").href;
    this.shadowRoot.appendChild(link);
  }
}

/**
 * "Ejects" from the shadow DOM.
 * Advantage: The element and it's children will be styled according to (cascading) global style selectors.
 * Disadvantage: slots won't work within these elements.
 */
class LitElementEjected extends LitElement {
  createRenderRoot() {
    // Returning `this` will render the template directly into the light DOM
    return this;
  }
}

class SubscriptionWidget extends LitElementEjected {
  static get properties() {
    return {
      price: { type: Number },
      product_id: { type: String },
      variant_id: { type: String },
      selected_variant: { type: Object },
      selected_plan: { type: Object },
      frequency_label: { type: String },
      quantity: { type: Number },
      quantity_label: { type: String },
      state: { type: String },
      weight: { type: Number },
      unavailable_ids: { type: String },
      caption: { type: String },
      caption_list: { type: String },
    };
  }

  constructor() {
    super();
    this.state = "loading";
    this.quantity = 1;
    this.variants = [];
    this.plans = [];
    this.selected_plan = null;
    this.sub_only = false;
    this.weight = 12;
    this._updateQuantity = this._updateQuantity.bind(this);
    this._updateFrequency = this._updateFrequency.bind(this);
    this.selectedOption = "subscriptions";
    this.slashedPrice = undefined;
    this.actualPrice = undefined;
  }

  connectedCallback() {
    super.connectedCallback();
    this._init();
  }

  async _init() {
    await this._login();

    this.variant_input = document.querySelector('input[name="id"]');
    if (this.variant_input) {
      this.variant_input.addEventListener(
        "change",
        this._updateVariant.bind(this)
      );
    }

    this.state = "idle";
  }

  async _login() {
    this.session = await recharge.auth.loginShopifyAppProxy();
    const settings = await recharge.cdn.getCDNProductAndSettings(
      this.product_id
    );

    this.store_settings = settings.storeSettings;
    this.widget_settings = settings.widgetSettings;
    this.product_settings = settings.product;
    const productQuery = await recharge.product.productSearch(this.session, {
      format_version: "2022-06",
      external_product_ids: [this.product_id],
    });

    this.product = productQuery.products[0];

    this.variants = this.product_settings.variants.filter(
      (v) => v.is_published != false
    );
    this.selected_variant = this.variants.find(
      (v) => Number(v.id) === Number(this.variant_id)
    );

    this.plans = this.product.plans.filter((plan) => plan.type !== "onetime");
    this.plans = this.plans.sort((a, b) => a.sort_order - b.sort_order);
    this.selected_plan = this.plans.length > 0 ? this.plans[0] : null;

    this.sub_only =
      !this.product_settings.is_subscription_only &&
      !this.product_settings.isSubscriptionOnly;

    this.actualPrice = this.selected_variant.prices.unit_price;
    if (this.selected_plan) {
      if (
        !this.selected_variant.selling_plan_allocations ||
        this.selected_variant.selling_plan_allocations.length === 0
      )
        return;
      const nextPrice = this.selected_variant.selling_plan_allocations.find(
        (price) =>
          price.selling_plan_id.toString() ===
          this.selected_plan.external_plan_id.toString()
      );

      if (!nextPrice) return;
      this.actualPrice = `$${nextPrice.discounted_price}`;
      this.slashedPrice = `$${this.selected_variant.prices.unit_price}`;
    }
  }

  _handleRadioChange(event) {
    this.selectedOption = event.target.value;
    this.requestUpdate();
  }

  async _formSubmit(event) {
    event.preventDefault(); // Prevent default form submission

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/cart/add";

    // Create input for variant ID
    const variantInput = document.createElement("input");
    variantInput.type = "hidden";
    variantInput.name = "id";
    variantInput.value = this.selected_variant.id;
    form.appendChild(variantInput);

    // Check if it's a subscription and add the selling plan input
    if (this.selectedOption === "subscriptions" && this.selected_plan) {
      const sellingPlanInput = document.createElement("input");
      sellingPlanInput.type = "hidden";
      sellingPlanInput.name = "selling_plan";
      sellingPlanInput.value = this.selected_plan.external_plan_id;
      form.appendChild(sellingPlanInput);
    }

    // Create input for quantity
    const quantityInput = document.createElement("input");
    quantityInput.type = "hidden";
    quantityInput.name = "quantity";
    quantityInput.value = this.quantity;
    form.appendChild(quantityInput);

    // Append the form to the document and submit it
    document.body.appendChild(form);
    form.submit();
  }

  sanitizeHTML(html) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Remove all <script> elements and their content
    const scripts = tempDiv.querySelectorAll("script");
    scripts.forEach((script) => script.remove());

    return tempDiv.innerHTML;
  }

  render() {
    if (this.state === "loading") {
      return;
    }
    const sanitizedCaptionItems = this.sanitizeHTML(this.caption_list);

    return html`
      <form id="${this.product_id}-add-item-form" @submit=${this._formSubmit}>
        <div class="sms-recharge-widget__container">
          <fieldset>
            <div
              class="sms-recharge-widget__radio-option ${this.selectedOption ===
              "subscriptions"
                ? "sms-rw__radio-selected"
                : "sms-rw__radio-unselected"}"
            >
              <label for="subscriptions">
                <div class="sms-rw__radio-label-container">
                  <div class="sms-rw__radio-label-left">
                    <div class="sms-rw__flex">
                      <input
                        type="radio"
                        id="${this.product_id}-subscriptions"
                        name="subscriptions"
                        value="subscriptions"
                        @change=${this._handleRadioChange}
                        ?checked=${this.selectedOption === "subscriptions"}
                      />
                    </div>
                    <span class="sms-rw__radio-label">Subscribe and save</span>
                  </div>
                  <div>
                    <div class="sms-rw__slashed-price">
                      ${this.slashedPrice}
                    </div>
                    <div class="sms-rw__actual-price">${this.actualPrice}</div>
                  </div>
                </div>
                <div class="sms-rw__radio-label-caption">
                  <div class="sms-rw__radio-label-caption__title">
                    ${this.caption}
                  </div>

                  <div class="sms-rw__caption-list">
                    ${unsafeHTML(sanitizedCaptionItems)}
                  </div>
                </div>
                <select
                  class="sms-widget__select"
                  name="sms-frequency"
                  id="sms-frequency"
                  @change=${this._updateFrequency}
                >
                  ${this.plans.map((plan) => this._renderFrequencyOption(plan))}
                </select>
              </label>
            </div>

            <div
              class="sms-recharge-widget__radio-option ${this.selectedOption ===
              "one-time-purchase"
                ? "sms-rw__radio-selected"
                : "sms-rw__radio-unselected"}"
            >
              <label for="one-time-purchase">
                <div class="sms-rw__radio-label-container">
                  <div class="sms-rw__radio-label-left">
                    <div class="sms-rw__flex">
                      <input
                        type="radio"
                        id="${this.product_id}-one-time-purchase"
                        name="subscriptions"
                        value="one-time-purchase"
                        @change=${this._handleRadioChange}
                        ?checked=${this.selectedOption === "one-time-purchase"}
                      />
                    </div>
                    <span class="sms-rw__radio-label">One-time purchase</span>
                  </div>
                  <div>
                    <div class="sms-rw__actual-price">
                      $${this.selected_variant.prices.unit_price}
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </fieldset>
          <button
            id="add-to-cart"
            class="add-to-cart product-card-button-container sms-rw__button"
            type="submit"
            name="add"
          >
            <span>Add to cart</span>
          </button>
        </div>
      </form>
    `;
  }

  _renderFrequencyOption(plan) {
    if (
      plan.external_variant_ids.length > 0 &&
      !plan.external_variant_ids.find(
        (id) => Number(id.ecommerce) === Number(this.variant_id)
      )
    )
      return;

    return html`
      <option
        value="${plan.id}"
        data-interval="${plan.subscription_preferences
          .order_interval_frequency}"
        ${this.selected_plan.id === plan.id ? html`selected` : ""}
      >
        ${plan.title.replace("Onetime", "One-time")}
      </option>
    `;
  }

  _updateQuantity(e) {
    this.quantity = e.target.value;
  }

  _updateFrequency(e) {
    this.selected_plan = this.plans.find(
      (p) => Number(p.id) === Number(e.target.value)
    );
  }

  _updateVariant(e) {
    this.selected_variant = this.variants.find(
      (v) => Number(v.id) === Number(e.target.value)
    );
  }
}
customElements.define("sms-recharge-widget", SubscriptionWidget);
